/**
 * Confronto fra due release del prodotto ("Confronta release"): tipi della risposta di
 * GET /api/products/products/:id/releases/compare e resa "stile git diff" in righe.
 *
 * Il server confronta metadati, bundle (abbinati per nome), file BOM e componenti dichiarati nei
 * BOM; qui si traduce il risultato in righe `+`/`-`/contesto raggruppate in blocchi `@@`.
 */

export interface ComparedComponent {
  name: string;
  version?: string;
  group?: string;
  purl?: string;
  type?: string;
}

export interface ComponentChange {
  key: string;
  name: string;
  group?: string;
  fromVersion?: string;
  toVersion?: string;
}

export interface BomRef {
  filename?: string;
  format: 'sbom' | 'hbom';
}

export type BundleStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export interface BundleDiff {
  key: string;
  label: string;
  status: BundleStatus;
  boms: { added: BomRef[]; removed: BomRef[]; unchanged: BomRef[] };
  components: {
    added: ComparedComponent[];
    removed: ComparedComponent[];
    changed: ComponentChange[];
    unchangedCount: number;
  };
  unreadableBoms: { from: number; to: number };
}

export type ReleaseField = 'version' | 'marketEntryDate' | 'supportEndDate' | 'cpeSuffix' | 'purl';

export interface ReleaseComparison {
  from: string;
  to: string;
  fields: { field: ReleaseField; from?: string; to?: string; changed: boolean }[];
  bundles: BundleDiff[];
  summary: {
    fieldsChanged: number;
    bundlesAdded: number;
    bundlesRemoved: number;
    bundlesChanged: number;
    componentsAdded: number;
    componentsRemoved: number;
    componentsChanged: number;
  };
}

/** Tipo di riga del diff: aggiunta, rimozione, contesto, intestazione file/blocco, avviso. */
export type DiffLineKind = 'add' | 'del' | 'ctx' | 'file' | 'hunk' | 'warn';

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

export const RELEASE_FIELD_LABEL: Record<ReleaseField, string> = {
  version: 'Versione',
  marketEntryDate: 'Immissione sul mercato',
  supportEndDate: 'Fine supporto',
  cpeSuffix: 'CPE suffix',
  purl: 'PURL',
};

const STATO_BUNDLE: Record<BundleStatus, string> = {
  added: 'aggiunto',
  removed: 'rimosso',
  changed: 'modificato',
  unchanged: 'invariato',
};

const PREFISSO: Record<DiffLineKind, string> = {
  add: '+ ',
  del: '- ',
  ctx: '  ',
  file: '',
  hunk: '',
  warn: '! ',
};

/** Testo della riga come in un diff unificato (prefisso `+`/`-`/spazio). */
export function diffLineText(line: DiffLine): string {
  return `${PREFISSO[line.kind]}${line.text}`;
}

function nomeComponente(c: { name: string; group?: string }): string {
  return c.group ? `${c.group}/${c.name}` : c.name;
}

function conVersione(c: { name: string; group?: string }, version?: string): string {
  return version ? `${nomeComponente(c)}@${version}` : nomeComponente(c);
}

function nomeBom(b: BomRef): string {
  return `BOM ${b.filename ?? '(senza nome)'} [${b.format.toUpperCase()}]`;
}

/**
 * Righe del diff "stile git": intestazione `--- from` / `+++ to`, blocco metadati della release
 * (solo i campi valorizzati; quelli cambiati come coppia -/+), poi un blocco `@@` per ogni bundle
 * con file BOM e componenti (aggiunti, rimossi, versione cambiata come coppia -/+).
 * I bundle invariati restano una sola riga di contesto.
 */
export function releaseDiffLines(c: ReleaseComparison): DiffLine[] {
  const righe: DiffLine[] = [
    { kind: 'file', text: `--- release ${c.from}` },
    { kind: 'file', text: `+++ release ${c.to}` },
    { kind: 'hunk', text: '@@ Release @@' },
  ];

  for (const f of c.fields) {
    const label = RELEASE_FIELD_LABEL[f.field];
    if (!f.changed) {
      if (f.to !== undefined) righe.push({ kind: 'ctx', text: `${label}: ${f.to}` });
      continue;
    }
    if (f.from !== undefined) righe.push({ kind: 'del', text: `${label}: ${f.from}` });
    if (f.to !== undefined) righe.push({ kind: 'add', text: `${label}: ${f.to}` });
  }

  if (c.bundles.length === 0) {
    righe.push({ kind: 'ctx', text: 'Nessun bundle in entrambe le release.' });
  }

  for (const b of c.bundles) {
    const comp = b.components;
    if (b.status === 'unchanged') {
      righe.push({
        kind: 'hunk',
        text: `@@ Bundle «${b.label}» — invariato (${comp.unchangedCount} componenti) @@`,
      });
      continue;
    }
    righe.push({ kind: 'hunk', text: `@@ Bundle «${b.label}» — ${STATO_BUNDLE[b.status]} @@` });
    for (const bom of b.boms.removed) righe.push({ kind: 'del', text: nomeBom(bom) });
    for (const bom of b.boms.added) righe.push({ kind: 'add', text: nomeBom(bom) });
    for (const bom of b.boms.unchanged) righe.push({ kind: 'ctx', text: nomeBom(bom) });
    for (const x of comp.removed) righe.push({ kind: 'del', text: conVersione(x, x.version) });
    for (const x of comp.added) righe.push({ kind: 'add', text: conVersione(x, x.version) });
    for (const x of comp.changed) {
      righe.push({ kind: 'del', text: conVersione(x, x.fromVersion) });
      righe.push({ kind: 'add', text: conVersione(x, x.toVersion) });
    }
    if (comp.unchangedCount > 0) {
      righe.push({ kind: 'ctx', text: `… ${comp.unchangedCount} componenti invariati` });
    }
    const illeggibili = b.unreadableBoms.from + b.unreadableBoms.to;
    if (illeggibili > 0) {
      righe.push({
        kind: 'warn',
        text: `${illeggibili} BOM non interpretabili (componenti non confrontati)`,
      });
    }
  }
  return righe;
}

/** Riepilogo compatto ("2 campi, bundle +1 −1 ~1, componenti +3 −1 ~2"). */
export function releaseDiffSummary(c: ReleaseComparison): string {
  const s = c.summary;
  const nessuna =
    s.fieldsChanged +
      s.bundlesAdded +
      s.bundlesRemoved +
      s.bundlesChanged +
      s.componentsAdded +
      s.componentsRemoved +
      s.componentsChanged ===
    0;
  if (nessuna) return 'Nessuna differenza fra le due release.';
  return [
    `${s.fieldsChanged} ${s.fieldsChanged === 1 ? 'campo' : 'campi'} della release`,
    `bundle +${s.bundlesAdded} −${s.bundlesRemoved} ~${s.bundlesChanged}`,
    `componenti +${s.componentsAdded} −${s.componentsRemoved} ~${s.componentsChanged}`,
  ].join(' · ');
}
